<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { AppTypography } from '~/components/base';

type CampaignTypeFilter = 'all' | 'community' | 'party';

const visible = defineModel<boolean>('visible', { default: false });
const modelValue = defineModel<CampaignTypeFilter>({ default: 'all' });

const OPTIONS: { value: CampaignTypeFilter; label: string }[] = [
  { value: 'all', label: 'All (default)' },
  { value: 'community', label: 'Candidate Led Campaigns' },
  { value: 'party', label: 'Party Led Campaigns' },
];

const select = (value: CampaignTypeFilter) => {
  modelValue.value = value;
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    header="Filters"
    class="app-modal campaign-filter w-full max-w-md"
    :dismissable-mask="true"
  >
    <div class="flex flex-col gap-4">
      <ul class="flex flex-col">
        <li
          v-for="option in OPTIONS"
          :key="option.value"
          :data-testid="`campaign-filter-${option.value}`"
          class="campaign-filter__row flex cursor-pointer items-center justify-between py-6"
          role="button"
          tabindex="0"
          @click="select(option.value)"
          @keydown.enter="select(option.value)"
          @keydown.space.prevent="select(option.value)"
        >
          <AppTypography type="body">{{ option.label }}</AppTypography>
          <i
            v-if="modelValue === option.value"
            class="pi pi-check campaign-filter__check"
          />
        </li>
      </ul>
    </div>
  </Dialog>
</template>

<!--
  The dialog is teleported to <body> and carries no scoped data-v attribute,
  so scoped :deep() can't reach its chrome (header/content/panel). The
  `.campaign-filter` class *is* on the .p-dialog panel, so the sheet chrome is
  themed in the unscoped block below; MY slot content (rows) keeps its
  scoped styles here.
-->
<style scoped lang="scss">
.campaign-filter {
  &__row {
    // Warm tan divider that reads on the sand sheet (Figma) instead of the
    // cool grey used on white surfaces.
    border-bottom: 1px solid $complementary-color-shade-1;

    &:last-of-type {
      border-bottom: none;
    }
  }

  &__check {
    color: $text-color-brand;
    font-size: 1rem;
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog chrome via the
  `.campaign-filter` class it carries on the .p-dialog panel. Kept narrow to
  this modal so it doesn't leak to other dialogs.
-->
<style lang="scss">
.campaign-filter.p-dialog {
  // Warm sand sheet to match Figma (replaces the default white dialog).
  background-color: #ece3d1;

  .p-dialog-header,
  .p-dialog-content {
    background-color: #ece3d1;
  }

  // Positioning context for the close button below — VTW-555 fixed the
  // title to flush-left on desktop, but the mobile full-screen sheet
  // recenters it (same convention as LocationPickerModal.vue: mobile keeps
  // a centered title with the close button pinned to the corner
  // independent of flex flow; desktop drops the centered treatment).
  .p-dialog-header {
    position: relative;

    @media (max-width: 639.98px) {
      justify-content: center;
    }
  }

  .p-dialog-title {
    @media (max-width: 639.98px) {
      text-align: center;
    }
  }

  .p-dialog-close-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    box-shadow: none;

    // Bare X, no background, coloured to match the other modals' mobile
    // close button ($text-color-subtext, Figma's text/primary on this sand
    // sheet). Alignment follows the base `_dialog.scss` inset
    // ($spacing-sm) — the same offset used by the candidates.index Party
    // filter (CandidatePartyModal.vue), so all mobile filter sheets line up
    // flush with the content below.
    @media (max-width: 639.98px) {
      color: $text-color-subtext;
    }

    // Aligns the button's right edge with the row content's right edge (the
    // header and content regions share the same 24px inset from the panel
    // edge) — same fix as CandidatesModal.vue's close button. `!important`
    // is required: the shared `.app-modal.app-modal.p-dialog .p-dialog-header
    // .p-dialog-close-button` rule in `_dialog.scss` sets `right:
    // $spacing-sm` at 5 class-selectors of specificity, which beats this
    // rule's 3 regardless of source order.
    //
    // PrimeVue's default close button only paints its light-grey circular
    // background on `:hover` — at rest it's transparent. Figma's desktop
    // reference shows that circle persistently, at rest, so it's pinned on
    // unconditionally here, matching CandidatesModal.vue's close-button
    // treatment (same tokens, same 28px footprint, same desktop-only scope —
    // the mobile full-screen reference keeps a bare X with no circle).
    @media (min-width: 640px) {
      right: $spacing-xl !important;
      width: 28px;
      height: 28px;
      padding: 0;
      color: $border-color-tag;
      background-color: $surface-color-bg-2;

      &:hover,
      &:enabled:hover {
        background-color: $surface-color-bg-2;
      }
    }
  }
}
</style>
